/**
 * Grade-level utilities — single source of truth for "is this a young learner?"
 *
 * BUG-FIX (2026-04-25): Multiple components used `gradeLevel.includes("1")`
 * and `gradeLevel.includes("2")` to classify K-2 students. That substring
 * match incorrectly tagged Grade 11, 12, "9-12", "11-12", etc. as young
 * learners, which triggered the 8-second auto-continue loop in
 * `InvisibleTeacher` for high-school students — the "looping" issue.
 *
 * This helper extracts the FIRST grade token from any common format
 * ("K", "K-2", "Grade 5", "9-12", "11", "12") and classifies based on
 * that value: K, 1, or 2 → young; anything else → not young.
 */
export function isYoungLearnerGrade(gradeLevel) {
  if (!gradeLevel || typeof gradeLevel !== "string") return false;
  const normalized = gradeLevel.toUpperCase().trim();

  // Match the first grade token: a leading "K" OR a leading 1- or 2-digit number.
  // Examples → captured value:
  //   "K"        → "K"
  //   "K-2"      → "K"
  //   "GRADE 1"  → "1"
  //   "1-2"      → "1"
  //   "9-12"     → "9"
  //   "11"       → "11"
  //   "12"       → "12"
  const match = normalized.match(/(?:^|[^A-Z0-9])(K|\d{1,2})(?:[^0-9]|$)/);
  if (!match) {
    // Last-resort: bare K or single digit
    if (/^K$/.test(normalized)) return true;
    return false;
  }
  const token = match[1];
  if (token === "K") return true;
  const n = parseInt(token, 10);
  return Number.isFinite(n) && n >= 1 && n <= 2;
}

/** Numeric grade extracted from a string, or null. K → 0. */
export function getNumericGrade(gradeLevel) {
  if (!gradeLevel || typeof gradeLevel !== "string") return null;
  const normalized = gradeLevel.toUpperCase().trim();
  const match = normalized.match(/(?:^|[^A-Z0-9])(K|\d{1,2})(?:[^0-9]|$)/);
  if (!match) return /^K$/.test(normalized) ? 0 : null;
  return match[1] === "K" ? 0 : parseInt(match[1], 10);
}
